import { Tooltip } from "antd";
import {
  WalletOutlined, ThunderboltOutlined, FileProtectOutlined, TeamOutlined,
  CheckSquareOutlined, FileTextOutlined, LoginOutlined,
  SolutionOutlined, UsergroupAddOutlined,
} from "@ant-design/icons";
import { inr, num, dateTime, relative, StatTile, UsageBar } from "./shared";

/**
 * Whether anyone is actually on this account right now.
 *
 * Three states, not two, because "signed in" and "here" are different facts: a
 * session can be valid for days while nobody has touched it for an afternoon.
 * Checked in order — online outranks a live session, which outranks none.
 */
const PRESENCE = {
  online: { label: "Online now", color: "#16a34a", bg: "#f0fdf4", border: "#bbf7d0" },
  session: { label: "Logged in", color: "#b45309", bg: "#fffbeb", border: "#fde68a" },
  out: { label: "Logged out", color: "#64748b", bg: "#f1f5f9", border: "#e2e8f0" },
};

const presenceOf = (summary) => {
  if (summary.online_now === true) return PRESENCE.online;
  if (summary.session_active === true) return PRESENCE.session;
  return PRESENCE.out;
};

const PresenceChip = ({ state }) => (
  <span style={{
    display: "inline-flex", alignItems: "center", gap: 6, marginTop: 4,
    padding: "2px 10px", borderRadius: 20, fontSize: 11, fontWeight: 700,
    color: state.color, background: state.bg, border: `1px solid ${state.border}`,
  }}>
    {/* A dot rather than an emoji: it takes the chip's own colour, so the two
        never disagree, and it renders the same on every platform. */}
    <span style={{ width: 7, height: 7, borderRadius: "50%", background: state.color, flexShrink: 0 }} />
    {state.label}
  </span>
);

/**
 * The account-side stat cards on Overview.
 *
 * The existing Overview answers "what is in this portfolio". These answer "what
 * is the state of the account behind it" — what they have left to spend, how
 * many people are on it, what is still open. Clicking one opens the tab that
 * explains it, so a number is never a dead end.
 */
const AccountSummaryCards = ({ summary, loading, onOpenTab }) => {
  if (loading) {
    return (
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(215px,1fr))", gap: 16, marginBottom: 24 }}>
        {Array.from({ length: 9 }).map((_, i) => (
          <div key={i} style={{
            height: 96, borderRadius: 14, background: "#fff",
            border: "1px solid rgba(0,0,0,0.06)", boxShadow: "0 1px 2px rgba(0,0,0,0.04)",
          }} />
        ))}
      </div>
    );
  }

  if (!summary) return null;

  const credits = summary.ai_credits || {};

  const cards = [
    {
      tab: "wallet",
      node: <StatTile icon={<WalletOutlined />} label="Wallet balance" value={inr(summary.wallet_balance)} color="#6366f1" bg="#eef2ff" />,
    },
    {
      tab: "ai-credits",
      node: (
        <StatTile icon={<ThunderboltOutlined />} label="AI credits" value={num(credits.balance)} color="#7c3aed" bg="#f5f3ff">
          <UsageBar used={credits.used} total={credits.purchased} color="#7c3aed" />
        </StatTile>
      ),
    },
    {
      tab: "tm-breakdown",
      node: (
        /* Both figures come from the API as they are — nothing is summed here.
           Monitoring sits on its own line rather than beside the headline
           because it overlaps it: it counts how many of these are watched, not
           a second kind of trademark to be added on. */
        <StatTile
          icon={<FileProtectOutlined />} label="Subscribed trademarks"
          value={num(summary.total_tm_subscribed)}
          sub={(
            <span style={{ color: "#d97706", fontWeight: 600 }}>
              {num(summary.total_tm_monitoring)} in monitoring
            </span>
          )}
          color="#0d9488" bg="#f0fdfa"
        />
      ),
    },
    {
      tab: "clients",
      node: <StatTile icon={<SolutionOutlined />} label="Clients" value={num(summary.clients)} color="#0891b2" bg="#ecfeff" />,
    },
    {
      tab: "client-groups",
      node: <StatTile icon={<UsergroupAddOutlined />} label="Client groups" value={num(summary.client_groups)} color="#7c3aed" bg="#f5f3ff" />,
    },
    {
      tab: "team",
      node: <StatTile icon={<TeamOutlined />} label="Team members" value={num(summary.team_members)} color="#2563eb" bg="#eff6ff" />,
    },
    {
      tab: "tasks",
      node: <StatTile icon={<CheckSquareOutlined />} label="Open tasks" value={num(summary.open_tasks)} color="#ea580c" bg="#fff7ed" />,
    },
    {
      tab: "notice-replies",
      node: (
        <StatTile
          icon={<FileTextOutlined />} label="Notice replies" value={num(summary.notice_replies)}
          sub={`${num(summary.notice_replies_generated)} generated`}
          color="#db2777" bg="#fdf2f8"
        />
      ),
    },
    {
      tab: "logins",
      node: (
        /* Relative up front — "2h ago" is what tells you whether this account is
           alive. The exact timestamp is one hover away for when it matters, and
           the chip below says whether that session is still live. */
        <Tooltip title={summary.last_login ? dateTime(summary.last_login) : "No login recorded"}>
          <div style={{ height: "100%" }}>
            <StatTile
              icon={<LoginOutlined />} label="Last login"
              value={relative(summary.last_login)}
              color="#0891b2" bg="#ecfeff"
            >
              <PresenceChip state={presenceOf(summary)} />
            </StatTile>
          </div>
        </Tooltip>
      ),
    },
  ];

  return (
    <div style={{ marginBottom: 24 }}>
      {/* Labelled, because Overview now carries two rows of numbers and they
          answer different questions — this row is the account, the row below is
          the portfolio. */}
      <div style={{
        fontSize: 11.5, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase",
        letterSpacing: 0.6, marginBottom: 10,
      }}>
        Account
      </div>
      <div style={{
        display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(215px,1fr))",
        gap: 16, alignItems: "stretch",
      }}>
        {cards.map((c) => (
          <div
            key={c.tab}
            onClick={() => onOpenTab?.(c.tab)}
            style={{ cursor: "pointer" }}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onOpenTab?.(c.tab); } }}
          >
            {c.node}
          </div>
        ))}
      </div>
    </div>
  );
};

export default AccountSummaryCards;
